/**
 * React Query hooks for fetching small UPS configurations
 * @see GitLab #996 — Repeating Sections (Phase 1: Small UPS)
 */

import { useQuery } from '@tanstack/react-query';

import { isRepeatingCollectionRouteNotFound } from './repeatingCollectionRouteNotFound';
import { useNetworkStatus } from './useOfflineRequestsQuery';
import { groupSmallUpsByStatus } from '../../components/smallups/smallUpsListUtils';
import { api } from '../../services/api';
import {
  applyPendingSmallUpsOperations,
  getCachedSmallUpsList,
  getOfflineSmallUpsById,
  getOfflineSmallUpsGrouped,
  getOfflineSmallUpsList,
  getInFlightSmallUpsOpsForRequest,
  mirrorSmallUpsToOfflineRequestCache,
} from '../../services/smallUpsOfflineSync';

import type {
  SmallUpsConfiguration,
  SmallUpsConfigurationsResponse,
  GroupedSmallUpsConfigurations,
} from '../../types/smallUps';

/**
 * Query keys for consistent cache management
 */
export const smallUpsConfigurationsKeys = {
  all: ['smallUpsConfigurations'] as const,
  lists: () => [...smallUpsConfigurationsKeys.all, 'list'] as const,
  list: (requestId: string) =>
    [...smallUpsConfigurationsKeys.lists(), requestId] as const,
  grouped: (requestId: string) =>
    [...smallUpsConfigurationsKeys.all, 'grouped', requestId] as const,
  details: () => [...smallUpsConfigurationsKeys.all, 'detail'] as const,
  detail: (requestId: string, smallUpsId: number) =>
    [...smallUpsConfigurationsKeys.details(), requestId, smallUpsId] as const,
};

/**
 * Hook for fetching all small UPS configurations for a request
 * Includes offline fallback to return cached configurations
 */
export const useSmallUpsConfigurations = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: smallUpsConfigurationsKeys.list(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineSmallUpsList(requestId);
      }

      try {
        const response = await api.get<SmallUpsConfigurationsResponse>(
          `/requests/${requestId}/small-ups`,
        );
        await mirrorSmallUpsToOfflineRequestCache(requestId, response.smallUps);
        const pendingOps = await getInFlightSmallUpsOpsForRequest(requestId);
        return applyPendingSmallUpsOperations(
          requestId,
          response.smallUps,
          pendingOps,
        );
      } catch (error) {
        if (isRepeatingCollectionRouteNotFound(error)) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) =>
      isRepeatingCollectionRouteNotFound(error) ? false : failureCount < 3,
    meta: {
      errorMessage: 'Failed to fetch small UPS configurations.',
    },
  });
};

/**
 * Hook for fetching small UPS configurations grouped by status (existing vs new)
 * Includes offline fallback (derived from cached list)
 */
export const useSmallUpsConfigurationsGrouped = (
  requestId: string,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: smallUpsConfigurationsKeys.grouped(requestId),
    queryFn: async () => {
      if (!isOnline) {
        return getOfflineSmallUpsGrouped(requestId);
      }

      const response = await api.get<GroupedSmallUpsConfigurations>(
        `/requests/${requestId}/small-ups/grouped`,
      );
      const allUnits = [...response.existing, ...response.new];
      await mirrorSmallUpsToOfflineRequestCache(requestId, allUnits);
      const pendingOps = await getInFlightSmallUpsOpsForRequest(requestId);
      return groupSmallUpsByStatus(
        applyPendingSmallUpsOperations(requestId, allUnits, pendingOps),
      );
    },
    enabled: !!requestId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch grouped small UPS configurations.',
    },
  });
};

/**
 * Hook for fetching a single small UPS configuration
 * Includes offline fallback from cached request units
 */
export const useSmallUpsConfiguration = (
  requestId: string,
  smallUpsId: number,
  enabled = true,
) => {
  const isOnline = useNetworkStatus();

  return useQuery({
    queryKey: smallUpsConfigurationsKeys.detail(requestId, smallUpsId),
    queryFn: async () => {
      if (!isOnline) {
        const cached = await getOfflineSmallUpsById(requestId, smallUpsId);
        if (!cached) {
          throw new Error('Small UPS configuration not found in offline cache');
        }
        return cached;
      }

      const response = await api.get<SmallUpsConfiguration>(
        `/requests/${requestId}/small-ups/${smallUpsId}`,
      );
      const list = await getCachedSmallUpsList(requestId);
      const merged = list.some(u => u.id === response.id)
        ? list.map(u => (u.id === response.id ? response : u))
        : [...list, response];
      await mirrorSmallUpsToOfflineRequestCache(requestId, merged);
      return response;
    },
    enabled: !!requestId && !!smallUpsId && enabled,
    staleTime: 30 * 1000,
    meta: {
      errorMessage: 'Failed to fetch small UPS configuration details.',
    },
  });
};
